import React, { useState } from 'react';
import { fmt, fyLabel, Card, Badge, benchmarkTone } from './dprFields';
import { exportCaseWord } from './dprExport';

const DSCR_THRESHOLDS = [
  { min: -Infinity, tone: 'rose', label: 'Below 1.00x — likely rejected' },
  { min: 1.00, tone: 'amber', label: '1.00–1.24x — marginal' },
  { min: 1.25, tone: 'emerald', label: '≥1.25x — bankable' },
  { min: 1.50, tone: 'emerald', label: '≥1.50x — strong' },
];
const CURRENT_RATIO_THRESHOLDS = [
  { min: -Infinity, tone: 'rose', label: 'Below 1.33x — weak' },
  { min: 1.33, tone: 'emerald', label: '≥1.33x — meets manufacturing norm' },
];
const TOL_TNW_THRESHOLDS = [
  { min: -Infinity, tone: 'emerald', label: '≤3.0x — comfortable' },
  { min: 3.0, tone: 'amber', label: '3.0–4.0x — watch' },
  { min: 4.0, tone: 'rose', label: 'Above 4.0x — highly leveraged' },
];
const DEBT_EQUITY_THRESHOLDS = [
  { min: -Infinity, tone: 'emerald', label: '≤2.0x — comfortable' },
  { min: 2.0, tone: 'amber', label: '2.0–3.0x — watch' },
  { min: 3.0, tone: 'rose', label: 'Above 3.0x — highly leveraged' },
];
function BenchmarkBadge({ value, thresholds }) {
  const t = benchmarkTone(value, thresholds);
  return <Badge tone={t.tone}>{fmt(value)}{value !== null && value !== undefined ? 'x' : ''} · {t.label}</Badge>;
}

// The "Ratio Highlighting Panel" — a bank-underwriter's-eye view of the four
// covenants that actually decide whether a proposal gets rejected (DSCR,
// Current Ratio, TOL/TNW, Debt-Equity), always visible at the top of the
// Summary tab so a case that's drifting against lending capacity is obvious
// before it ever reaches a bank, not discovered after submission.
function RatioHighlightPanel({ schedules, baseFY }) {
  const lastRatio = schedules.ratios[schedules.ratios.length - 1] || {};
  const lastYearLabel = fyLabel(baseFY, schedules.ratios.length);
  const items = [
    { label: 'Average DSCR', value: schedules.dscr.averageDSCR, thresholds: DSCR_THRESHOLDS },
    { label: `Current Ratio (${lastYearLabel})`, value: lastRatio.currentRatio, thresholds: CURRENT_RATIO_THRESHOLDS },
    { label: `TOL/TNW (${lastYearLabel})`, value: lastRatio.tolToTNW, thresholds: TOL_TNW_THRESHOLDS },
    { label: `Debt-Equity (${lastYearLabel})`, value: lastRatio.debtEquity, thresholds: DEBT_EQUITY_THRESHOLDS },
  ];
  const anyRed = items.some(i => benchmarkTone(i.value, i.thresholds).tone === 'rose');
  return (
    <Card className={`p-4 mb-5 border-2 ${anyRed ? 'border-rose-300 dark:border-rose-700' : 'border-emerald-300 dark:border-emerald-700'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-extrabold text-slate-700 dark:text-slate-200 uppercase tracking-wide">Bankability &amp; Lending Capacity Check</div>
        <Badge tone={anyRed ? 'rose' : 'emerald'}>{anyRed ? 'One or more covenants weak' : 'Within normal lending capacity'}</Badge>
      </div>
      <div className="grid grid-cols-4 gap-4">
        {items.map(i => (
          <div key={i.label}>
            <div className="text-[11px] text-slate-400 mb-1">{i.label}</div>
            <BenchmarkBadge value={i.value} thresholds={i.thresholds} />
          </div>
        ))}
      </div>
    </Card>
  );
}

function ScheduleTable({ years, rows }) {
  return (
    <Card className="overflow-x-auto mb-6 p-0">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gradient-to-r from-slate-50 to-[#0f1f45]/[0.03] dark:from-slate-900 dark:to-orange-500/5">
            <th className="text-left px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">Particulars</th>
            {years.map(y => <th key={y} className="text-right px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">{typeof y === 'number' ? `Yr ${y}` : y}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={row.bold ? 'font-bold bg-slate-50/50 dark:bg-slate-900/40' : ''}>
              <td className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{row.label}</td>
              {row.values.map((v, j) => <td key={j} className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const TABS = [
  'Summary', 'Profitability Projections', 'Balance Sheet Projections', 'Cash Flow Projections',
  'Working Capital Assessment', 'DSCR · Ratios · Sensitivity', 'Break-Even Analysis', 'Loan Repayment Schedule',
  'CMA Form I', 'CMA Form II', 'CMA Form III', 'CMA Form IV', 'CMA Form V', 'CMA Form VI', 'CMA Form VII',
];
// Old internal ids kept as the `tab` state values so the switch statements below don't churn.
const TAB_ID = {
  'Summary': 'Summary', 'Profitability Projections': 'P&L', 'Balance Sheet Projections': 'Balance Sheet',
  'Cash Flow Projections': 'Cash Flow', 'Working Capital Assessment': 'Working Capital',
  'DSCR · Ratios · Sensitivity': 'DSCR & Ratios', 'Break-Even Analysis': 'BEP', 'Loan Repayment Schedule': 'Repayment',
  'CMA Form I': 'Form I', 'CMA Form II': 'Form II', 'CMA Form III': 'Form III',
  'CMA Form IV': 'Form IV', 'CMA Form V': 'Form V', 'CMA Form VI': 'Form VI', 'CMA Form VII': 'Form VII',
};
const FORM_TITLES = {
  'Form I': 'Particulars of Existing / Proposed Limits', 'Form II': 'Operating Statement', 'Form III': 'Analysis of Balance Sheet',
  'Form IV': 'Comparative Statement of Current Assets & Current Liabilities', 'Form V': 'Computation of Maximum Permissible Bank Finance (MPBF)',
  'Form VI': 'Funds Flow Statement', 'Form VII': 'Ratio Analysis Dashboard',
};

export default function CaseReport({ caseData, schedules, mode }) {
  const [tab, setTab] = useState('Summary');
  if (!schedules || schedules.error) {
    return <div className="text-sm text-red-500 p-4">{schedules?.error || 'No computed schedules yet — fill in the Setup tab and save.'}</div>;
  }
  const isCma = mode === 'cma_only';
  const visibleTabs = isCma ? TABS.filter(t => t !== 'Cash Flow Projections' && t !== 'Break-Even Analysis') : TABS;
  const years = schedules.profitLoss.map(p => p.year);
  // Real-DPR "2026-27" style labels, anchored to the Business Profile step's
  // Project Start Financial Year — falls back to plain "Yr N" for any case
  // that hasn't set one. Every ScheduleTable below uses these, not the raw
  // year-index numbers.
  const baseFY = caseData.business_profile?.baseFinancialYear;
  const yearLabels = years.map(y => fyLabel(baseFY, y));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1.5 flex-wrap">
          {visibleTabs.map(label => (
            <button key={label} onClick={() => setTab(TAB_ID[label])}
              style={tab === TAB_ID[label] ? { background: 'linear-gradient(135deg, #0f1f45, #1a3a6b)' } : undefined}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === TAB_ID[label] ? 'text-white shadow-sm' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <button onClick={() => exportCaseWord(caseData, schedules)}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white text-xs font-bold shadow-sm hover:shadow-md">
            ⬇ Download Full Report (Word)
          </button>
        </div>
      </div>

      {tab === 'Summary' && (
        <>
          <Card className="p-4 mb-5 bg-[#0f1f45]/[0.03] dark:bg-orange-500/5 border-[#0f1f45]/10 dark:border-orange-500/20">
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              <b>CMA data</b> answers "can you repay?" with standardized financial forms — mandatory per RBI guidelines above ₹10 Lacs.
              A <b>DPR</b> answers "will this work?" with market and technical narrative. Below is your CMA numeric package; DSCR and Current Ratio are graded against real bank benchmarks.
            </p>
          </Card>

          {!isCma && <RatioHighlightPanel schedules={schedules} baseFY={baseFY} />}

          <div className="grid grid-cols-3 gap-4 mb-6">
            {!isCma && <SummaryCard label="Cost of Project" value={fmt(schedules.costOfProjectTotal)} />}
            {!isCma && <SummaryCard label="Bank Financed (of Cost of Project)" value={`${fmt(schedules.bankFinancedBreakdown?.total)} / ${fmt(schedules.costOfProjectTotal)}`} />}
            {isCma && <SummaryCard label="Proposed CC / WC Limit" value={fmt((caseData.company_profile || {}).proposedCCLimit)} />}
            {isCma && <SummaryCard label="Combined Term Loan (Closing)" value={fmt(schedules.repayment.byYear[schedules.repayment.byYear.length - 1]?.closingBalance)} />}
            <SummaryCard label={`Net Profit (${yearLabels[yearLabels.length - 1]})`} value={fmt(schedules.profitLoss[schedules.profitLoss.length - 1]?.npat)} />
          </div>

          {!isCma && schedules.costOfProjectSchedule && (
            <Card className="overflow-x-auto mb-6 p-0">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm font-extrabold text-[#001254] dark:text-slate-100">Cost of Project</div>
                {schedules.costOfProjectSchedule.termLoanAmount > 0 && (
                  schedules.costOfProjectSchedule.overAllocated > 0.01
                    ? <Badge tone="rose">Bank Finance ₹{fmt(schedules.costOfProjectSchedule.totalBankFinance)} exceeds Term Loan Sanctioned ₹{fmt(schedules.costOfProjectSchedule.termLoanAmount)} by ₹{fmt(schedules.costOfProjectSchedule.overAllocated)}</Badge>
                    : schedules.costOfProjectSchedule.unallocated > 0.01
                      ? <Badge tone="amber">₹{fmt(schedules.costOfProjectSchedule.unallocated)} of the sanctioned Term Loan not yet allocated to a category</Badge>
                      : <Badge tone="emerald">Bank Finance fully allocated — matches Term Loan Sanctioned ₹{fmt(schedules.costOfProjectSchedule.termLoanAmount)}</Badge>
                )}
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 to-[#0f1f45]/[0.03] dark:from-slate-900 dark:to-orange-500/5">
                    <th className="text-left px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">Particulars</th>
                    <th className="text-right px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">Amount</th>
                    <th className="text-right px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">Promoter Margin</th>
                    <th className="text-right px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">Bank Finance</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.costOfProjectSchedule.categories.filter(c => c.total > 0).map((c, ci) => (
                    <React.Fragment key={ci}>
                      <tr className="font-bold bg-slate-50/50 dark:bg-slate-900/40">
                        <td className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{c.name}</td>
                        <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(c.total)}</td>
                        <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(c.margin)}</td>
                        <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(c.bankFinance)}</td>
                      </tr>
                      {c.items.length > 1 && c.items.map((it, ii) => (
                        <tr key={ii}>
                          <td className="pl-8 pr-4 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-400 italic">{it.name}</td>
                          <td className="text-right px-4 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-400 italic">{fmt(it.amount)}</td>
                          <td className="border-b border-slate-100 dark:border-slate-800"></td>
                          <td className="border-b border-slate-100 dark:border-slate-800"></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {schedules.costOfProjectSchedule.preop > 0 && (
                    <tr>
                      <td className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Pre-Operative Expenses</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(schedules.costOfProjectSchedule.preop)}</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(schedules.costOfProjectSchedule.preop)}</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">-</td>
                    </tr>
                  )}
                  {schedules.costOfProjectSchedule.idcp > 0 && (
                    <tr>
                      <td className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Interest During Construction Period (IDCP)</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(schedules.costOfProjectSchedule.idcp)}</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(schedules.costOfProjectSchedule.idcp)}</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">-</td>
                    </tr>
                  )}
                  {schedules.costOfProjectSchedule.securityDeposits > 0 && (
                    <tr>
                      <td className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Security Deposits</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(schedules.costOfProjectSchedule.securityDeposits)}</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(schedules.costOfProjectSchedule.securityDeposits)}</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">-</td>
                    </tr>
                  )}
                  {schedules.costOfProjectSchedule.workingCapitalMargin > 0 && (
                    <tr>
                      <td className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Working Capital Margin</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(schedules.costOfProjectSchedule.workingCapitalMargin)}</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(schedules.costOfProjectSchedule.workingCapitalMargin)}</td>
                      <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">-</td>
                    </tr>
                  )}
                  <tr className="font-extrabold bg-orange-500/10">
                    <td className="px-4 py-2.5 text-[#001254] dark:text-slate-100">Total Cost of Project</td>
                    <td className="text-right px-4 py-2.5 text-[#001254] dark:text-slate-100">{fmt(schedules.costOfProjectSchedule.total)}</td>
                    <td className="text-right px-4 py-2.5 text-[#001254] dark:text-slate-100">{fmt(schedules.costOfProjectSchedule.totalMargin)}</td>
                    <td className="text-right px-4 py-2.5 text-[#001254] dark:text-slate-100">{fmt(schedules.costOfProjectSchedule.totalBankFinance)}</td>
                  </tr>
                </tbody>
              </table>
            </Card>
          )}

          {!isCma && schedules.subsidy?.amount > 0 && (
            <Card className="p-4 mb-6 border-amber-200 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-500/5">
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">
                Subsidy: {schedules.subsidy.schemeName} — ₹{fmt(schedules.subsidy.amount)}
                {' '}({schedules.subsidyIncludedInBaseCase ? 'included in base case' : 'excluded from base case — shown below as upside'})
              </div>
              {!schedules.subsidyIncludedInBaseCase && schedules.subsidyUpside && (
                <div className="grid grid-cols-4 gap-4 mt-3">
                  <div><div className="text-[11px] text-slate-400 mb-1">Avg DSCR with Subsidy</div><div className="text-sm font-bold text-slate-700 dark:text-slate-200">{fmt(schedules.subsidyUpside.averageDSCR)}x</div></div>
                  <div><div className="text-[11px] text-slate-400 mb-1">IRR with Subsidy</div><div className="text-sm font-bold text-slate-700 dark:text-slate-200">{schedules.subsidyUpside.irrPct === null ? 'n/a' : `${fmt(schedules.subsidyUpside.irrPct)}%`}</div></div>
                  <div><div className="text-[11px] text-slate-400 mb-1">NPV with Subsidy</div><div className="text-sm font-bold text-slate-700 dark:text-slate-200">₹{fmt(schedules.subsidyUpside.npv)}</div></div>
                  <div><div className="text-[11px] text-slate-400 mb-1">Debt-Equity with Subsidy</div><div className="text-sm font-bold text-slate-700 dark:text-slate-200">{fmt(schedules.subsidyUpside.lastYearDebtEquity)}x</div></div>
                </div>
              )}
            </Card>
          )}

          {schedules.bankability && (
            <div className="grid grid-cols-3 gap-4 mb-2">
              <SummaryCard label={`Project IRR (@ ${schedules.bankability.discountRatePct}% discount)`} value={schedules.bankability.irrPct === null ? 'n/a' : `${fmt(schedules.bankability.irrPct)}%`} />
              <SummaryCard label="NPV" value={fmt(schedules.bankability.npv)} />
              <SummaryCard label="Avg. EBITDA Margin" value={`${fmt(schedules.bankability.avgEbitdaMarginPct)}%`} />
            </div>
          )}
        </>
      )}

      {tab === 'P&L' && schedules.sales?.byYear?.[0]?.products?.length > 1 && (
        <div className="mb-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Sales — Product / By-Product Breakdown</div>
          <ScheduleTable years={yearLabels} rows={
            schedules.sales.byYear[0].products.flatMap((_, pi) => {
              const name = schedules.sales.byYear[0].products[pi].name;
              const isByProduct = schedules.sales.byYear[0].products[pi].kind === 'by_product';
              const unit = schedules.sales.byYear[0].products[pi].unit;
              return [
                { label: `${name}${isByProduct ? ' (By-Product)' : ' (Main Product)'} — Net Sales Qty${unit ? ` (${unit})` : ''}`, values: schedules.sales.byYear.map(y => y.products[pi]?.netSalesQty) },
                { label: `${name} — Sales Value`, values: schedules.sales.byYear.map(y => y.products[pi]?.salesValue), bold: true },
              ];
            }).concat([{ label: 'Total Sales Value (all Products & By-Products)', values: schedules.sales.byYear.map(y => y.salesValue), bold: true }])
          } />
        </div>
      )}
      {tab === 'P&L' && (() => {
        const pl = schedules.profitLoss;
        const rmLineNames = (pl[0]?.rmLines || []).map(l => l.name);
        // First raw material row reads plain "Raw Material Consumed" — any
        // further row (a secondary material, e.g. a second fruit/solvent)
        // gets its name appended, matching how a real DPR's P&L only
        // disambiguates once there's more than one to tell apart.
        const rmRows = rmLineNames.map((name, li) => ({
          label: li === 0 ? 'Raw Material Consumed' : `Raw Material Consumed (${name})`,
          values: pl.map(p => p.rmLines?.[li]?.cost),
        }));
        const taxRate = caseData.tax_rate_pct ?? 26;
        return (
          <ScheduleTable years={yearLabels} rows={[
            { label: 'Gross Sales', values: pl.map(p => p.grossSale) },
            ...rmRows,
            { label: 'Labour & Wages', values: pl.map(p => p.labourCost) },
            { label: 'Packaging', values: pl.map(p => p.packaging) },
            { label: 'Custom Direct Expenses (Power & Fuel, Insurance, R&M, etc.)', values: pl.map(p => p.customExpenses) },
            { label: 'Depreciation', values: pl.map(p => p.depreciation) },
            { label: 'Cost of Production', values: pl.map(p => p.costOfProduction), bold: true },
            { label: 'Add: Opening Stock / WIP', values: pl.map(p => p.openingStockValue) },
            { label: 'Less: Closing Stock / WIP', values: pl.map(p => p.closingStockValue) },
            { label: 'Cost of Sales', values: pl.map(p => p.costOfSales), bold: true },
            { label: 'Gross Profit', values: pl.map(p => p.grossProfit), bold: true },
            { label: 'Gross Profit Ratio (%)', values: pl.map(p => p.grossProfitPct) },
            { label: 'Salary to Staff', values: pl.map(p => p.staffSalary) },
            { label: 'Interest on Term Loan', values: pl.map(p => p.tlInterest) },
            { label: 'Interest on Working Capital Loan', values: pl.map(p => p.wcInterest) },
            { label: 'Selling & Other Expense', values: pl.map(p => p.sellingExp + (p.indirectExpenses || 0)) },
            { label: 'Total', values: pl.map(p => p.staffSalary + p.tlInterest + p.wcInterest + p.sellingExp + (p.indirectExpenses || 0)), bold: true },
            { label: 'Other Income (Subsidy Release — AS-12 Deferred Income)', values: pl.map(p => p.otherIncome || 0) },
            { label: 'Net Profit Before Tax', values: pl.map(p => p.npbt), bold: true },
            { label: `Taxation (${fmt(taxRate)}%)`, values: pl.map(p => p.tax) },
            { label: 'Net Profit After Tax', values: pl.map(p => p.npat), bold: true },
            { label: 'Net Profit Ratio (%)', values: pl.map(p => p.npatPct) },
          ]} />
        );
      })()}

      {tab === 'Balance Sheet' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Capital', values: schedules.balanceSheet.map(b => b.capital) },
        { label: 'Reserves & Surplus', values: schedules.balanceSheet.map(b => b.reserves) },
        { label: 'Term Loan', values: schedules.balanceSheet.map(b => b.termLoan) },
        { label: 'WC Borrowing', values: schedules.balanceSheet.map(b => b.wcBorrowing) },
        { label: 'Creditors', values: schedules.balanceSheet.map(b => b.creditors) },
        { label: 'Capital Reserve (Govt Subsidy — AS-12)', values: schedules.balanceSheet.map(b => b.capitalReserve || 0) },
        { label: 'Deferred Govt Grant (AS-12)', values: schedules.balanceSheet.map(b => b.deferredGovtGrant || 0) },
        { label: 'Total Liabilities', values: schedules.balanceSheet.map(b => b.totalLiabilities), bold: true },
        { label: 'Freehold Land', values: schedules.balanceSheet.map(b => b.freeholdLand) },
        { label: 'Net Fixed Assets', values: schedules.balanceSheet.map(b => b.netBlock) },
        { label: 'Cash & Bank', values: schedules.balanceSheet.map(b => b.cash) },
        { label: 'Debtors', values: schedules.balanceSheet.map(b => b.debtors) },
        { label: 'Stock', values: schedules.balanceSheet.map(b => b.stock) },
        { label: 'Total Assets', values: schedules.balanceSheet.map(b => b.totalAssets), bold: true },
        { label: 'Balance Check (should trend to 0)', values: schedules.balanceSheet.map(b => b.balanceCheck) },
      ]} />}

      {tab === 'Cash Flow' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Opening Cash', values: schedules.cashFlow.map(c => c.opening) },
        { label: 'Sources', values: schedules.cashFlow.map(c => c.sources) },
        { label: 'Uses', values: schedules.cashFlow.map(c => c.uses) },
        { label: 'Net Cash', values: schedules.cashFlow.map(c => c.netCash) },
        { label: 'Closing Cash', values: schedules.cashFlow.map(c => c.closing), bold: true },
      ]} />}

      {tab === 'Working Capital' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Debtors', values: schedules.workingCapital.byYear.map(w => w.debtors) },
        { label: 'Creditors', values: schedules.workingCapital.byYear.map(w => w.creditors) },
        { label: 'Raw Material Stock', values: schedules.workingCapital.byYear.map(w => w.rmStock) },
        { label: 'Finished Goods Stock', values: schedules.workingCapital.byYear.map(w => w.fgStock) },
        { label: 'Total Stock', values: schedules.workingCapital.byYear.map(w => w.stock), bold: true },
        { label: 'Bank Finance / MPBF', values: schedules.workingCapital.byYear.map(w => w.mpbf ?? w.bankFinance ?? w.totalWC), bold: true },
      ]} />}

      {tab === 'DSCR & Ratios' && <>
        <ScheduleTable years={yearLabels} rows={[
          { label: 'Net Operating Income', values: schedules.dscr.rows.map(r => r.netOperatingIncome) },
          { label: 'Debt Service', values: schedules.dscr.rows.map(r => r.debtService) },
          { label: 'DSCR', values: schedules.dscr.rows.map(r => r.dscr), bold: true },
        ]} />
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Average DSCR: {fmt(schedules.dscr.averageDSCR)}x</span>
          <BenchmarkBadge value={schedules.dscr.averageDSCR} thresholds={DSCR_THRESHOLDS} />
        </div>
        <ScheduleTable years={yearLabels} rows={[
          { label: 'Gross Profit Ratio %', values: schedules.ratios.map(r => r.grossProfitRatio) },
          { label: 'PAT / Turnover %', values: schedules.ratios.map(r => r.patToTurnover) },
          { label: 'PAT / TNW %', values: schedules.ratios.map(r => r.patToTNW) },
          { label: 'ROCE %', values: schedules.ratios.map(r => r.roce) },
          { label: 'Interest Coverage', values: schedules.ratios.map(r => r.interestCoverage) },
          { label: 'Current Ratio', values: schedules.ratios.map(r => r.currentRatio) },
          { label: 'TOL / TNW', values: schedules.ratios.map(r => r.tolToTNW) },
          { label: 'Debt-Equity', values: schedules.ratios.map(r => r.debtEquity) },
        ]} />
      </>}

      {tab === 'BEP' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Variable Expenses', values: schedules.bep.map(b => b.variableExp) },
        { label: 'Fixed Expenses', values: schedules.bep.map(b => b.fixedExp) },
        { label: 'Contribution', values: schedules.bep.map(b => b.contribution) },
        { label: 'BEP Sales Value', values: schedules.bep.map(b => b.bepSalesValue), bold: true },
        { label: 'BEP %', values: schedules.bep.map(b => b.bepPct) },
        { label: 'EBIT', values: schedules.bep.map(b => b.ebit) },
        { label: 'EBITDA', values: schedules.bep.map(b => b.ebitda) },
        { label: 'EBITDA Margin %', values: schedules.bep.map(b => b.ebitdaMarginPct) },
        { label: 'ISCR (Interest Service Coverage)', values: schedules.bep.map(b => b.iscr) },
      ]} />}

      {tab === 'Repayment' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Opening Balance', values: schedules.repayment.byYear.map(r => r.openingBalance) },
        { label: 'Interest', values: schedules.repayment.byYear.map(r => r.interest) },
        { label: 'Installment', values: schedules.repayment.byYear.map(r => r.installment) },
        { label: 'Closing Balance', values: schedules.repayment.byYear.map(r => r.closingBalance), bold: true },
      ]} />}

      {tab.startsWith('Form') && (
        <div className="text-xs font-bold text-[#0f1f45] dark:text-orange-300 mb-3 -mt-1">{tab} — {FORM_TITLES[tab]}</div>
      )}

      {tab === 'Form I' && (
        <Card className="overflow-x-auto mb-6 p-0">
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-slate-50 dark:bg-slate-900">
              <th className="text-left px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">Particulars</th>
              <th className="text-right px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">Existing</th>
              <th className="text-right px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-400 uppercase tracking-wide text-[10px]">Proposed</th>
            </tr></thead>
            <tbody>
              {schedules.forms.formI.facilities.map((f, i) => (
                <tr key={i}><td className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{f.particulars}</td>
                  <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800">{fmt(f.existing)}</td>
                  <td className="text-right px-4 py-2 border-b border-slate-100 dark:border-slate-800">{fmt(f.proposed)}</td></tr>
              ))}
              <tr className="font-bold bg-slate-50/50 dark:bg-slate-900/40"><td className="px-4 py-2">Total</td>
                <td className="text-right px-4 py-2">{fmt(schedules.forms.formI.totalExisting)}</td>
                <td className="text-right px-4 py-2">{fmt(schedules.forms.formI.totalProposed)}</td></tr>
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'Form II' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Net Sales', values: schedules.forms.formII.map(f => f.netSales) },
        { label: 'Cost of Sales', values: schedules.forms.formII.map(f => f.costOfSales) },
        { label: 'Operating Profit before Interest', values: schedules.forms.formII.map(f => f.operatingProfitBeforeInterest) },
        { label: 'Interest', values: schedules.forms.formII.map(f => f.interest) },
        { label: 'Operating Profit after Interest', values: schedules.forms.formII.map(f => f.operatingProfitAfterInterest) },
        { label: 'Profit Before Tax', values: schedules.forms.formII.map(f => f.profitBeforeTax) },
        { label: 'Tax', values: schedules.forms.formII.map(f => f.tax) },
        { label: 'Net Profit', values: schedules.forms.formII.map(f => f.netProfit), bold: true },
      ]} />}

      {tab === 'Form III' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Total Current Liabilities', values: schedules.forms.formIII.map(f => f.totalCurrentLiabilities) },
        { label: 'Total Term Liabilities', values: schedules.forms.formIII.map(f => f.totalTermLiabilities) },
        { label: 'Total Outside Liabilities', values: schedules.forms.formIII.map(f => f.totalOutsideLiabilities) },
        { label: 'Net Worth', values: schedules.forms.formIII.map(f => f.netWorth) },
        { label: 'Total Current Assets', values: schedules.forms.formIII.map(f => f.totalCurrentAssets) },
        { label: 'Net Block', values: schedules.forms.formIII.map(f => f.netBlock) },
        { label: 'Total Assets', values: schedules.forms.formIII.map(f => f.totalAssets), bold: true },
        { label: 'Tangible Net Worth', values: schedules.forms.formIII.map(f => f.tangibleNetWorth) },
        { label: 'Net Working Capital', values: schedules.forms.formIII.map(f => f.netWorkingCapital) },
        { label: 'Current Ratio', values: schedules.forms.formIII.map(f => f.currentRatio) },
        { label: 'TOL / TNW', values: schedules.forms.formIII.map(f => f.tolToTNW) },
        { label: 'Term Liab / TNW', values: schedules.forms.formIII.map(f => f.termLiabToTNW) },
      ]} />}

      {tab === 'Form IV' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Raw Material & Stock', values: schedules.forms.formIV.map(f => f.rawMaterialAndStock) },
        { label: 'Receivables', values: schedules.forms.formIV.map(f => f.receivables) },
        { label: 'Other Current Assets', values: schedules.forms.formIV.map(f => f.otherCurrentAssets) },
        { label: 'Total Current Assets', values: schedules.forms.formIV.map(f => f.totalCurrentAssets), bold: true },
        { label: 'Creditors for Purchases', values: schedules.forms.formIV.map(f => f.creditorsForPurchases) },
        { label: 'Other Current Liabilities', values: schedules.forms.formIV.map(f => f.otherCurrentLiabilities) },
        { label: 'Total Current Liabilities', values: schedules.forms.formIV.map(f => f.totalCurrentLiabilities), bold: true },
      ]} />}

      {tab === 'Form V' && <ScheduleTable years={yearLabels} rows={
        schedules.forms.formV.method === 'traditional' ? [
          { label: 'Paid Stock', values: schedules.forms.formV.byYear.map(w => w.paidStock) },
          { label: 'Stock Bank Finance', values: schedules.forms.formV.byYear.map(w => w.stockBankFinance) },
          { label: 'Debtor Bank Finance', values: schedules.forms.formV.byYear.map(w => w.debtorBankFinance) },
          { label: 'MPBF (Traditional Method)', values: schedules.forms.formV.byYear.map(w => w.mpbf), bold: true },
        ] : schedules.forms.formV.method === 'turnover' ? [
          { label: 'Total Working Capital (20% of Turnover)', values: schedules.forms.formV.byYear.map(w => w.totalWC) },
          { label: 'Promoter Margin', values: schedules.forms.formV.byYear.map(w => w.margin) },
          { label: 'Bank Finance', values: schedules.forms.formV.byYear.map(w => w.bankFinance), bold: true },
        ] : [
          { label: 'Current Assets', values: schedules.forms.formV.byYear.map(w => w.currentAssets) },
          { label: 'Other Current Liabilities', values: schedules.forms.formV.byYear.map(w => w.otherCurrentLiabilities) },
          { label: 'MPBF (Method 2/3)', values: schedules.forms.formV.byYear.map(w => w.mpbf), bold: true },
          { label: 'Promoter Margin', values: schedules.forms.formV.byYear.map(w => w.promoterMargin) },
        ]
      } />}

      {tab === 'Form VI' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Sources', values: schedules.forms.formVI.map(f => f.sources) },
        { label: 'Uses', values: schedules.forms.formVI.map(f => f.uses) },
        { label: 'Long Term Surplus / Deficit', values: schedules.forms.formVI.map(f => f.longTermSurplus), bold: true },
        { label: 'Increase in Current Assets', values: schedules.forms.formVI.map(f => f.caIncrease) },
        { label: 'Increase in Current Liabilities', values: schedules.forms.formVI.map(f => f.clIncrease) },
        { label: 'Net Surplus / Deficit (should ≈ 0)', values: schedules.forms.formVI.map(f => f.netSurplus) },
        { label: 'Increase in Bank Borrowing', values: schedules.forms.formVI.map(f => f.bankBorrowingIncrease) },
      ]} />}

      {tab === 'Form VII' && <ScheduleTable years={yearLabels} rows={[
        { label: 'Gross Profit Ratio %', values: schedules.forms.formVII.rows.map(r => r.grossProfitRatio) },
        { label: 'PAT / Turnover %', values: schedules.forms.formVII.rows.map(r => r.patToTurnover) },
        { label: 'PAT / TNW %', values: schedules.forms.formVII.rows.map(r => r.patToTNW) },
        { label: 'ROCE %', values: schedules.forms.formVII.rows.map(r => r.roce) },
        { label: 'Interest Coverage', values: schedules.forms.formVII.rows.map(r => r.interestCoverage) },
        { label: 'Current Ratio', values: schedules.forms.formVII.rows.map(r => r.currentRatio) },
        { label: 'TOL / TNW', values: schedules.forms.formVII.rows.map(r => r.tolToTNW) },
        { label: 'Debt-Equity', values: schedules.forms.formVII.rows.map(r => r.debtEquity) },
        { label: 'DSCR', values: schedules.forms.formVII.rows.map(r => r.dscr), bold: true },
      ]} />}
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-[11px] font-semibold text-slate-400 mb-1">{label}</div>
      <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100">{value}</div>
    </Card>
  );
}
